import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, CircularProgress } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { StepProps } from '../../../components/_WMS/Stepper'
import { AsnReceptionState, AsnReceptionResult, eStepAsnReception } from '../types'
import { searchBySupplier, searchBySupplierOrigin, searchByBrand, searchByWarehouse } from '../api'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { useSuppliers } from '../../../hooks/useSuppliers'
import { useSuppliersOrigin } from '../../../hooks/useSuppliersOrigin'
import { useWarehouses, useProductOrigins } from '../../../hooks'
import { ApiResponse } from '../../../typings/ApiResponse'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'

type SearchOption = 'Proveedor' | 'Origen' | 'Marca' | 'Almacén'

interface UnknownIdStepProps extends StepProps<AsnReceptionState> {}


const itemSx = {
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: '#f5f5f5',
  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }
}

const chevronSx = {
  width: 44,
  backgroundColor: 'primary.main',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'stretch'
}

export const UnknownIdStep: React.FC<UnknownIdStepProps> = ({
  goToStep,
  setSharedState
}) => {
  const [searchBy, setSearchBy] = useState<SearchOption>('Proveedor')

  const { suppliersState: { suppliers }, getSuppliers } = useSuppliers()
  const { suppliersOriginState: { suppliersOrigin }, getSuppliersOrigin } = useSuppliersOrigin()
  const { productOriginsState: { productOrigins }, getProductOrigins } = useProductOrigins()
  const { warehousesState: { warehouses }, getWarehouses } = useWarehouses()
  const { callApi, state } = useApi()
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const filterOptions: SearchOption[] = ['Proveedor', 'Origen', 'Marca', 'Almacén']

  useEffect(() => {
    setSharedState((prev) => ({ ...prev, stepTitle: 'Busca la ASN por cualquiera de estos criterios' }))
    return () => {
      setSharedState((prev) => ({ ...prev, stepTitle: undefined }))
    }
  }, [])

  useEffect(() => {
    if (searchBy === 'Proveedor' && !suppliers?.length) getSuppliers()
    if (searchBy === 'Origen' && !suppliersOrigin?.length) getSuppliersOrigin()
    if (searchBy === 'Marca' && !productOrigins?.length) getProductOrigins()
    if (searchBy === 'Almacén' && !warehouses?.length) getWarehouses()
  }, [searchBy])

  const handleApiResponse = (response: ApiResponse<AsnReceptionResult>) => {
    if (response.totalItems === 0) {
      openSnackBar('No existe registro', false)
      return
    }
    const single = response.totalItems === 1 ? response.items[0] : null
    setSharedState((prev) => ({ ...prev, results: response.items, selectedResult: single }))
    if (response.totalItems === 1) {
      // TODO: ir al paso correspondiente según tenga LPN
      goToStep(eStepAsnReception.ResultsStep)
    } else {
      goToStep(eStepAsnReception.ResultsStep)
    }
  }

  const isLoading = state.status === Status.Loading
  const isLoadingSuppliers = searchBy === 'Proveedor' && !suppliers?.length
  const isLoadingOrigins = searchBy === 'Origen' && !suppliersOrigin?.length
  const isLoadingWarehouses = searchBy === 'Almacén' && !warehouses?.length

  if(isLoading)
    return (<Loading />)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', padding: 1.5, gap: 1, height: '100%', overflow: 'hidden' }}>
      <Box>
        <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
          Buscar por
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0 }}>
          {filterOptions.map((option) => (
            <Box key={option} sx={{ width: 'calc(50% - 4px)' }}>
              <Button
                fullWidth
                onClick={() => setSearchBy(option)}
                variant={searchBy === option ? 'contained' : 'outlined'}
                sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, py: 0.5, fontSize: '0.85rem' }}
              >
                {option}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Proveedor */}
      {searchBy === 'Proveedor' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          {isLoadingSuppliers && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
          <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {suppliers?.map((s) => (
              <Box key={s.supplierId} onClick={() => callApi(searchBySupplier(s.supplierId)).then(handleApiResponse).catch(() => openSnackBar('Error', false))} sx={itemSx}>
                <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>{s.supplierName}</Typography>
                <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Origen */}
      {searchBy === 'Origen' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          {isLoadingOrigins && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
          <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {suppliersOrigin?.map((o) => (
              <Box key={o.id} onClick={() => callApi(searchBySupplierOrigin(o.id)).then(handleApiResponse).catch(() => openSnackBar('Error', false))} sx={itemSx}>
                <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>{o.supplierOriginName}</Typography>
                <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Marca */}
      {searchBy === 'Marca' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          {!productOrigins?.length && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
          <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {productOrigins?.map((p) => (
              <Box key={p.productOriginId} onClick={() => callApi(searchByBrand(p.productOriginId)).then(handleApiResponse).catch(() => openSnackBar('Error', false))} sx={itemSx}>
                <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>{p.name}</Typography>
                <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Almacén */}
      {searchBy === 'Almacén' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant='overline' sx={{ fontWeight: 'bold', color: '#555', letterSpacing: 2 }}>
            Resultados
          </Typography>
          {isLoadingWarehouses && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
          <Box sx={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {warehouses?.map((w) => (
              <Box key={w.id} onClick={() => callApi(searchByWarehouse(w.id)).then(handleApiResponse).catch(() => openSnackBar('Error', false))} sx={itemSx}>
                <Typography variant='body2' sx={{ flex: 1, px: 2, py: 1.2, fontWeight: 500 }}>{w.warehouseName}</Typography>
                <Box sx={chevronSx}><ChevronRightIcon sx={{ color: '#fff', fontSize: 22 }} /></Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

<SnackBar message={message} open={isActive} status={isSuccess ? 'success' : 'error'} />
    </Box>
  )
}
